// Ask AI on the System log: "what did Dijana change yesterday?", "who approved lines this week",
// "show me Meir's changes this week". The page registers itself with the template's chat widget as
// Shipping does (`axai-shipping.ts`). Its tools read the entries the page reads (`/api/routing/changes`,
// `changes.ts`) — another range of days by the same route — and set the log's own filters (person,
// action, days, search) with Undo. They never write, and never restore.

import { useEffect, useRef } from "react";
import {
  APP_PAGES,
  ROUTING_API,
  SYSTEM_LOG_GUIDANCE,
  ACTION_WORDS,
  addDays,
  applyLogView,
  logMatches,
  logViewWords,
  querySystemLog,
  weekRange,
} from "@app/shared";
import type { ChangeView, ChangesPayload, LogFacts, LogQuery, LogView, LogViewInput, LogWords } from "@app/shared";
import { apiGet, buildQuery } from "@axny/platform-ui";
import type { AxaiApp, Tool } from "./axai-prompt";

export interface SystemLogAskPage {
  today: string;
  /** The log's filters Ask AI may set, as they are now. */
  view: LogView;
  apply: (next: LogView, unapplied: string[]) => void;
  /** Every entry of the page's days, with the Source and My customers as the reader set them. */
  rangeFacts: () => LogFacts[];
  /** The entries on screen now: the days and every filter. */
  shownFacts: () => LogFacts[];
  /** Entries of other days, read by the same route: what their own line text says. */
  factsOf: (changes: readonly ChangeView[]) => LogFacts[];
  words: LogWords;
}

const ACTIONS = Object.values(ACTION_WORDS).join(", ");

export const SYSTEM_LOG_TOOLS: Tool[] = [
  {
    name: "query_system_log",
    description:
      "Count, group and list System log entries — changes people made in the app — by person, action, line or " +
      "pick ticket, customer, date range and a text search over the note and the changes. Lists at most 20 " +
      "entries, each as the page says it (when, who, action, line, what changed). Within the page's days and " +
      "filters by default; give from / to for other days, ignore_view: true to drop the page's person, action " +
      "and search.",
    input_schema: {
      type: "object",
      properties: {
        person: { type: "array", items: { type: "string" }, description: "People's names or parts of them." },
        action: { type: "array", items: { type: "string" }, description: `Actions: ${ACTIONS}.` },
        line: { type: "string", description: "A line: its WMS #, customer · PTs · PO text, or id." },
        pick_ticket: { type: "string", description: "A pick ticket or SO #." },
        customer: { type: "array", items: { type: "string" }, description: "Customer names or parts of them." },
        from: { type: "string", description: "YYYY-MM-DD: the first day." },
        to: { type: "string", description: "YYYY-MM-DD: the last day." },
        search: { type: "string", description: "Text in the note or the changes (a value, a field)." },
        group_by: { type: "string", enum: ["person", "action", "day", "customer"] },
        ignore_view: { type: "boolean", description: "true only when the question asks past the page's filters." },
        limit: { type: "integer", description: "Entries to list, 0–20 (default 10). Counts cover every match." },
      },
    },
  },
  {
    name: "apply_view",
    description:
      "Set the System log's own filters — person, action, days, search — in one call; only what is named " +
      "changes ('clear' takes a part off). The user can undo it. The Source and My customers stay the user's.",
    input_schema: {
      type: "object",
      properties: {
        person: { type: "string", description: 'A person\'s name as the log shows it; "" for everyone.' },
        action: { type: "string", description: `One action: ${ACTIONS}; "" for every action.` },
        from: { type: "string", description: "YYYY-MM-DD." },
        to: { type: "string", description: "YYYY-MM-DD." },
        preset: { type: "string", enum: ["7d", "30d", "90d", "any"], description: "The page's day buttons." },
        search: { type: "string", description: "The search box." },
        clear: { type: "array", items: { type: "string", enum: ["person", "action", "search", "dates"] } },
      },
    },
  },
];

function systemPrompt(p: SystemLogAskPage) {
  const week = weekRange(p.today, "this");
  const monday = addDays(week.to, -6);
  return [
    {
      type: "text" as const,
      text: [
        "You are Ask AI in Production & Routing, the AXNY warehouse's app. The user is on the System log, with " +
          `${logViewWords(p.view)}. On screen: ${p.shownFacts().length} entries.`,
        APP_PAGES,
        `Today is ${p.today} (America/New_York); yesterday is ${addDays(p.today, -1)}; "this week" is ${monday} to ` +
          `${p.today} (Monday to today); "last month" is the calendar month before this one.`,
        "## Definitions",
        `- ${SYSTEM_LOG_GUIDANCE}`,
        "## Rules",
        "- Take every number from a tool answer. A count is within the page's days and filters unless the question " +
          "names other days or asks past them: say the scope.",
        "- You only read and set the log's filters: you cannot change, restore or delete anything, and must not " +
          "offer to. Restore is the page's own button.",
        "- When the question names a period ('yesterday', 'this week'), say its dates in the answer.",
        "- Answer briefly, with who, when and the line.",
      ].join("\n"),
    },
  ];
}

type QueryInput = LogQuery & { ignore_view?: boolean };

export function useSystemLogAskAi(page: SystemLogAskPage): void {
  const ref = useRef(page);
  ref.current = page;
  useEffect(() => {
    /** Entries of other days than the page's, read as the page reads its own. */
    const readDays = async (from?: string, to?: string) => {
      const body = await apiGet<ChangesPayload>(`${ROUTING_API}/changes${buildQuery({ from, to })}`);
      return ref.current.factsOf(body.changes);
    };
    /** Whether the page's days already hold `from`–`to`. */
    const inPageDays = (from?: string, to?: string) => {
      const v = ref.current.view;
      if (v.preset === "any") return true;
      return (!from || (!!v.from && from >= v.from)) && (!to || (!!v.to && to <= v.to)) && (!!from || !!to);
    };
    const app: AxaiApp = {
      systemPrompt: () => systemPrompt(ref.current),
      handleToolCall: async (name, input, appendStatus, isStale) => {
        const p = ref.current;
        try {
          if (name === "query_system_log") {
            const { ignore_view, ...q } = (input ?? {}) as QueryInput;
            appendStatus("Reading the System log…");
            const named = !!(q.from || q.to);
            const facts =
              named && !inPageDays(q.from, q.to)
                ? await readDays(q.from, q.to)
                : ignore_view || named
                  ? p.rangeFacts()
                  : p.shownFacts();
            return JSON.stringify({
              ...querySystemLog(facts, q, p.words),
              scope:
                named && !inPageDays(q.from, q.to)
                  ? `${q.from ?? "…"} to ${q.to ?? "…"}, every entry`
                  : ignore_view || named
                    ? `the page's days (${logViewWords({ ...p.view, person: "", action: "", search: "" })})`
                    : `the page's view (${logViewWords(p.view)})`,
            });
          }
          if (name === "apply_view") {
            if (isStale()) return "Not applied: the conversation moved on.";
            const q = (input ?? {}) as LogViewInput;
            const datesChange = !!(q.from || q.to || q.preset);
            // A person is one the log has in the days it will show.
            const days = datesChange ? applyLogView(p.view, { ...q, person: undefined }, []).view : p.view;
            const pool =
              datesChange && !inPageDays(days.from ?? undefined, days.to ?? undefined) && days.preset !== "any"
                ? await readDays(days.from ?? undefined, days.to ?? undefined)
                : p.rangeFacts();
            const people = [...new Set(pool.map((f) => f.change.personName).filter(Boolean))];
            const next = applyLogView(p.view, q, people);
            p.apply(next.view, next.unapplied);
            const shown = logMatches(
              pool.filter(
                (f) => (!next.view.from || f.day >= next.view.from) && (!next.view.to || f.day <= next.view.to),
              ),
              {
                person: next.view.person ? [next.view.person] : [],
                action: next.view.action ? [next.view.action] : [],
                search: next.view.search || undefined,
              },
              p.words,
            ).length;
            return JSON.stringify({
              applied: logViewWords(next.view),
              entries_shown: shown,
              not_applied: next.unapplied,
              undo: "The user can undo it with the Undo button under the Ask AI bar.",
            });
          }
          return JSON.stringify({ error: `Unknown tool ${name}` });
        } catch (e) {
          return JSON.stringify({ error: e instanceof Error ? e.message : String(e) });
        }
      },
    };
    window.__AXAI_APP__ = app;
    window.AXAI_TOOLS = SYSTEM_LOG_TOOLS;
    document.body.classList.add("axai-ready");
    return () => {
      if (window.__AXAI_APP__ === app) delete window.__AXAI_APP__;
      window.AXAI_TOOLS = [];
      document.body.classList.remove("axai-ready");
    };
  }, []);
}
